import { FunctionComponent, useCallback, useEffect, useRef } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import translate from '@i18n'
import useClickOutside from '@hooks/useClickOutside'
import useModalOverlayPortal from '@hooks/useModalOverlayPortal'
import IconButton from '@components/Buttons/IconButton'

type ModalProps = {
  onClose: () => void
  children: React.ReactNode
  title?: string
}

const Header = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    padding: 6px ${spacing16} 0 ${spacing16};
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`

const Title = styled.div`
  ${({
    theme: {
      typography: {
        headings: { small: smallHeadingCSS },
      },
    },
  }) => css`
    ${smallHeadingCSS}
  `}
`

const StyledWrapper = styled.div`
  ${({
    theme: {
      colors: { fillDefault },
      spacing: { spacing16 },
    },
  }) => css`
    height: 100vh;
    max-width: 632px;
    max-height: 632px;
    background-color: ${fillDefault};
    border-radius: 16px;
    overflow: hidden;

    display: flex;
    flex-direction: column;

    ${mobileCSS(css`
      width: calc(100vw - ${spacing16} * 2);
      max-height: 560px;
      margin: ${spacing16};
    `)};
  `}
`

const ModalWindow: FunctionComponent<ModalProps> = ({
  title,
  onClose,
  children,
}) => {
  const theme = useTheme()
  const { portalNode, enterModalOverlay, exitModalOverlay, InPortal } =
    useModalOverlayPortal()
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const onClickOutside = useCallback(() => {
    onClose()
  }, [onClose])

  useClickOutside({
    track: !!portalNode,
    elementRef: wrapperRef,
    onClickOutside,
  })

  useEffect(() => {
    enterModalOverlay()

    return () => {
      exitModalOverlay()
    }
  }, [enterModalOverlay, exitModalOverlay])

  return (
    portalNode && (
      <InPortal node={portalNode}>
        <StyledWrapper ref={wrapperRef}>
          {title && (
            <Header>
              <Title>{title}</Title>
              <IconButton
                ariaLabel={translate('close')}
                buttonSize={48}
                onClick={onClose}
                iconName="xmark-large"
                iconSize={24}
                iconColor={theme.colors.iconDefault}
              />
            </Header>
          )}
          {children}
        </StyledWrapper>
      </InPortal>
    )
  )
}

export default ModalWindow
